import React, { useEffect, useRef, useState } from "react";
import { Button, Card, CardContent, CardMedia, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import "./ImagePreview.css"; // Import external CSS file
import { GalleryCard, GalleryItem } from "./CardGallery";
import WZoom from 'vanilla-js-wheel-zoom';
import { Button as AntButton, Descriptions,DescriptionsProps,Tag,Input,Typography,Card as AntCard, TimelineItemProps, Space,Modal,message, Badge, Divider} from 'antd';
import { EditOutlined, PlusOutlined, LeftOutlined, RightOutlined, FileImageOutlined, CommentOutlined, InfoCircleOutlined } from '@ant-design/icons';
import DatasetCreationModal from "./DatasetCreation";
const { TextArea } = Input;

const { Paragraph, Text, Title } = Typography;
export interface ImageProps extends GalleryItem {
  timestamp: string;
  media_id: string;
  model: string;
  part_name: string;
  part_number: string;
  disposition: string;
  camera: string;
  system_name: string;
  path: string;
  defects: { x: number; y: number; w: number; h: number,confidence: number, comment?: string }[];
}

export interface OutflowImageProps extends GalleryItem {
  timestamp: string;
  media_id: string;
  model: string;
  part_name: string;
  part_number: string;
  camera: string;
  review_status: boolean;
  defects: { x: number; y: number; w: number; h: number }[];
  path: string;
}
// export interface ImageViewCard extends GalleryCard<ImageProps> {
//   showMeta: boolean;
// }
export interface ImageViewProps extends GalleryItem {
  image: ImageProps;
  updateDisposition: (val: string) => void;
  onDefectCommentUpdate?: (defectIndex: number, comment: string) => Promise<void>;
}


export interface MetadataProps {
  image: ImageProps;
  mutable_disposition: boolean;
  updateDisposition: (val: string) => void;
  className? : string
  onPrevious?: () => void;
  onNext?: () => void;
  onBack: () => void; 
  hasPrevious?: boolean;
  hasNext?: boolean;
}

interface WZoomInstance {
  destroy: () => void;
  maxZoomDown: () => void;
  maxZoomUp: () => void;
  // Add other methods you need
}

const getDispositionColor = (disposition: string) => {
  switch (disposition.toLowerCase()) {
    case "part okay":
      return "success";
    case "scrap":
      return "error";
    case "rework":
      return "warning";
    case "skip":
      return "processing";
    default:
      return "default";
  }
};
function get_review_style(reviewed: boolean) {
  if (!reviewed) {
    return 'error';
  }
  return 'success';

}


interface DefectCommentModalProps {
  defect: { x: number; y: number; w: number; h: number; confidence: number; comment?: string };
  defectIndex: number;
  visible: boolean;
  onClose: () => void;
  onSave: (comment: string) => void;
}

const DefectCommentModal: React.FC<DefectCommentModalProps> = ({
  defect,
  defectIndex,
  visible,
  onClose,
  onSave
}) => {
  const [comment, setComment] = useState(defect.comment || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setComment(defect.comment || '');
  }, [defect.comment, visible]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(comment);
      message.success('Comment saved successfully!');
      onClose();
    } catch (error: any) {
      if (error.message === 'DATASET_REQUIRED') {
        message.error('Cannot add comment: Image must be part of a dataset.');
      } else {
        message.error(error.message || 'Failed to save comment');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`Add Comment to Defect #${defectIndex + 1}`}
      open={visible}
      onCancel={onClose}
      footer={[
        <AntButton key="cancel" onClick={onClose}>
          Cancel
        </AntButton>,
        <AntButton key="save" type="primary" loading={loading} onClick={handleSave}>
          Save Comment
        </AntButton>,
      ]}
      width={500}
    >
      <div className="mb-4">
        <p><strong>Confidence:</strong> {defect.confidence}</p>
        <p><strong>Position:</strong> ({defect.x}, {defect.y})</p>
        <p><strong>Size:</strong> {defect.w} × {defect.h}</p>
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Comment:</label>
        <TextArea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add your comment about this defect..."
          rows={4}
          maxLength={500}
          showCount
        />
      </div>
    </Modal>
  );
};


export const ImageView: React.FC<ImageViewProps> = ({ 
  image, 
  updateDisposition,
  onDefectCommentUpdate // New prop to handle defect comment updates
}: ImageViewProps) => {
  const [ivcursor, setCursor] = useState('grab');
  const [selectedDefectIndex, setSelectedDefectIndex] = useState<number | null>(null);
  const [commentModalVisible, setCommentModalVisible] = useState(false);
  const wzoomRef = useRef<WZoomInstance | null>(null);

  useEffect(() => {
    // Initialize WZoom after the component mounts
    const instance = WZoom.create('#wzoom-content', {
      type: 'html',
      width: 4096,
      height: 3072,
      dragScrollableOptions: {
        smoothExtinction: 0.1,
        onGrab: () => { onGrabCb() },
        onDrop: () => { onDropCb() },
      },
      speed: 2,
      maxScale: 6,
      prepare: () => {
        return new Promise(resolve => setTimeout(resolve, 0))
      },
      rescale: () => { },
    });
    wzoomRef.current = instance;

    const handleResize = () => instance.prepare();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (wzoomRef.current) wzoomRef.current.destroy();
    };
  }, [ivcursor]);

  const handleZoomDown = () => {
    if (wzoomRef.current) {
      wzoomRef.current.maxZoomDown();
    }
  };

  function onGrabCb() {
    console.log("onGrabCb")
    setCursor('grabbing')
  }

  function onDropCb() {
    console.log("onDopCb")
    setCursor('grab')
  }

  const handleDefectClick = (index: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedDefectIndex(index);
    setCommentModalVisible(true);
  };

  const handleCommentSave = async (comment: string) => {
    if (selectedDefectIndex !== null && onDefectCommentUpdate) {
      await onDefectCommentUpdate(selectedDefectIndex, comment);
    }
  };

  const renderDefects = () => {
    // Check if image has defects
    if (!image.defects || !Array.isArray(image.defects) || image.defects.length === 0) {
      return null;
    }
    
    if (wzoomRef.current) {
      handleZoomDown();
    }
    
    return image.defects.map((defect, index) => {
      // Extract coordinates based on the actual structure
      const { x, y, w, h, confidence, comment } = defect;
      
      return (
        <div
          key={`defect-${index}`}
          className="defect-bbox group cursor-pointer hover:border-yellow-400 transition-colors duration-200"
          style={{
            position: 'absolute',
            left: `${x}px`,
            top: `${y}px`,
            width: `${w}px`,
            height: `${h}px`,
            border: 'solid 2px red',
            boxSizing: 'border-box',
            pointerEvents: 'auto', // Enable pointer events for clicking
            backgroundColor: 'rgba(255, 0, 0, 0.1)',
          }}
          onClick={(e) => handleDefectClick(index, e)}
          title={`Click to ${comment ? 'edit comment' : 'add comment'}`}
        >
          {/* Confidence label */}
          <span 
            style={{ 
              position: 'absolute', 
              top: '-20px', 
              left: '0', 
              backgroundColor: 'rgba(0, 0, 0, 0.7)', 
              color: 'white', 
              padding: '2px 5px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            {confidence}
            {comment && <CommentOutlined style={{ color: '#52c41a' }} />}
          </span>
          
          {/* Comment indicator - shows when there's a comment */}
          {comment && (
            <div
              style={{
                position: 'absolute',
                top: '2px',
                right: '2px',
                backgroundColor: 'rgba(82, 196, 26, 0.9)',
                borderRadius: '50%',
                width: '16px',
                height: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CommentOutlined style={{ fontSize: '10px', color: 'white' }} />
            </div>
          )}
          
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-blue-500 opacity-0 group-hover:opacity-20 transition-opacity duration-200 pointer-events-none" />
          
          {/* Click instruction on hover */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            <div className="bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
              <EditOutlined className="mr-1" />
              {comment ? 'Edit Comment' : 'Add Comment'}
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <>
      <div className="image-container">
        <div className={`image-view`} id={`wViewport`} style={{ cursor: ivcursor }}>
          <div id={`wzoom-content`}>
            <img 
              draggable="false" 
              className="relative flex items-center" 
              id={`wViewportImage`} 
              src={`${image.path}`} 
              alt={`${image.id}`} 
            />
            {renderDefects()}
          </div>
        </div>
      </div>

      {/* Comment Modal */}
      {selectedDefectIndex !== null && (
        <DefectCommentModal
          defect={image.defects[selectedDefectIndex]}
          defectIndex={selectedDefectIndex}
          visible={commentModalVisible}
          onClose={() => {
            setCommentModalVisible(false);
            setSelectedDefectIndex(null);
          }}
          onSave={handleCommentSave}
        />
      )}
    </>
  );
};




export interface EditLabelModalProps {
  image: ImageProps
}

export interface LabelOption {
  id: number;
  name: string;
}

export const EditLabelModal: React.FC<EditLabelModalProps> = ({ image }: EditLabelModalProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [labelOptions, setLabelOptions] = useState<LabelOption[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<number | string>('');
  const [newLabelName, setNewLabelName] = useState<string>('');

  useEffect(() => {
    fetchLabels()
  }, [labelOptions])

  const fetchLabels = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/labels`); // Adjust the API endpoint as necessary
      if (!response.ok) {
        throw new Error('Failed to fetch labels.');
      }
      const data = await response.json() as { labels: LabelOption[] };
      setLabelOptions(data.labels);
    } catch (error) {
      console.error('Error fetching labels:', error);
      // Optionally, set an error state to display an error message
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLabel('');
    setNewLabelName('');
  };

  const handleConfirmEditLabel = async () => {
    let labelId: number

    if (newLabelName.trim() !== '') {
      // Create a new label
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/labels`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newLabelName }),
        });
        if (!response.ok) {
          throw new Error('Failed to create a new label.');
        }
        const newLabel = await response.json();
        labelId = newLabel.id;
        setLabelOptions((prev) => [...prev, newLabel]); // Update the labels list
      } catch (error) {
        console.error('Error creating label:', error);
        alert('Error creating a new label. Please try again.');
        return;
      }
    } else if (selectedLabel !== '') {
      labelId = Number(selectedLabel);
    } else {
      // Neither a label is selected nor a new name is provided
      alert('Please select an existing label or enter a name for a new one.');
      return;
    }

    // Update the image's label
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/image/${image!.id}/edit_label`, { // Adjust the API endpoint as necessary
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label_id: labelId }),
      });
      if (!response.ok) {
        throw new Error('Failed to update the label.');
      }
      alert('Label successfully updated.');
      handleCloseModal();
      // // Optionally, refresh the image data to reflect the new label
      // fetchImage(image!.id);
    } catch (error) {
      console.error('Error updating label:', error);
      alert('Error updating label. Please try again.');
    }
  };

  return (<>
    {/* Edit Label Button */}
    <AntButton
        type="primary"
        disabled
        icon={<EditOutlined />}
        onClick={() => setIsModalOpen(true)}
        style={{ 
          backgroundColor: '#FFCC00', 
          borderColor: '#FFCC00',
          width: '100%',
          height: '40px'
        }}
        // className="hover:bg-yellow-500"
      >
        EDIT LABEL
      </AntButton>
    <Dialog open={isModalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
      <DialogTitle>Edit Label</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <InputLabel id="label-select-label">Select Label</InputLabel>
          <Select
            labelId="label-select-label"
            value={selectedLabel}
            label="Select Label"
            onChange={(e) => setSelectedLabel(e.target.value)}
          >
            {labelOptions.map((label) => (
              <MenuItem key={label.id} value={label.id}>
                {label.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          margin="normal"
          label="Or Create New Label"
          value={newLabelName}
          onChange={(e) => setNewLabelName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseModal} color="primary">
          Cancel
        </Button>
        <Button onClick={handleConfirmEditLabel} color="primary" variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>

  </>)
}




export interface AddToDatasetModalProps {
  image: ImageProps
}

interface Dataset {
  id: number;
  name: string;
}

export const ImagePreview: React.FC<GalleryCard<ImageProps>> = ({ item, onNavigate }) => {
  return (
    <Card className="image-preview" onClick={() => onNavigate(item.id)}>
      <CardMedia>
        <img src={`${process.env.REACT_APP_API_URL}/api/fetch_image/${item.id}`} alt={`${item.id}`} />
      </CardMedia>
    </Card>
  );
}

export const ImagePreviewMeta: React.FC<GalleryCard<ImageProps>> = ({ item, onNavigate }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hasDefects = item.defects && item.defects.length > 0;
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    // Only do the canvas drawing if defects exist.
    if (!hasDefects) return;

    const canvas = canvasRef.current;
    const image = new Image();
    if (!canvas) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    image.src = item.path;
    
    // Handle image loading errors
    image.onerror = () => {
      setImageError(true);
    };
    
    image.onload = () => {
      const devicePixelRatio = window.devicePixelRatio || 1;
      // Thumbnail dimensions (using canvas container size for the thumbnail)
      const thumbnailWidth = canvas.clientWidth || 400; // Default width if not set
      const thumbnailHeight = (image.height / image.width) * thumbnailWidth;

      // Set canvas size for rendering, scaled for high DPI
      canvas.width = thumbnailWidth * devicePixelRatio;
      canvas.height = thumbnailHeight * devicePixelRatio;
      canvas.style.width = `${thumbnailWidth}px`;
      canvas.style.height = `${thumbnailHeight}px`;

      // Scale the context for high DPI
      context.scale(devicePixelRatio, devicePixelRatio);

      // Draw the image onto the canvas at the thumbnail size
      context.drawImage(image, 0, 0, thumbnailWidth, thumbnailHeight);

      // Draw the defect boxes
      const defects = item.defects;
      context.strokeStyle = "red"; // Border color
      context.lineWidth = 1;
      context.globalAlpha = 0.3; // Transparency for fill
      context.fillStyle = "rgba(255, 0, 0, 0.3)";

      defects.forEach((defect) => {
        // Convert absolute pixel values to percentages of the full image
        const xPercent = defect.x / image.width;
        const yPercent = defect.y / image.height;
        const wPercent = defect.w / image.width;
        const hPercent = defect.h / image.height;

        // Draw filled rectangle scaled to the thumbnail dimensions
        context.fillRect(
          xPercent * thumbnailWidth,
          yPercent * thumbnailHeight,
          wPercent * thumbnailWidth,
          hPercent * thumbnailHeight
        );

        // Draw border rectangle on top
        context.globalAlpha = 1; // Fully opaque for the border
        context.strokeRect(
          xPercent * thumbnailWidth,
          yPercent * thumbnailHeight,
          wPercent * thumbnailWidth,
          hPercent * thumbnailHeight
        );
      });

      // Reset alpha to default for future operations
      context.globalAlpha = 1;
    };
  }, [item, hasDefects]);

  const renderImageContent = () => {
    if (!item.path || imageError) {
      return (
        <div className="flex flex-col items-center justify-center h-full w-full">
          <FileImageOutlined style={{ fontSize: "64px", color: "#d9d9d9" }} />
          <Typography.Text type="secondary" style={{ marginTop: "16px" }}>
            No Image Available
          </Typography.Text>
        </div>
      );
    }

    if (hasDefects) {
      return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", objectFit: "cover" }} />;
    } else {
      return (
        <img 
          src={item.path} 
          alt="Preview" 
          style={{ width: "100%", height: "100%", objectFit: "cover" }} 
          onError={() => setImageError(true)}
        />
      );
    }
  };


  return (
    <AntCard
      hoverable
      className="outflow-card image-preview-card overflow-hidden rounded-lg shadow-md transition-all w-full"
      onClick={() => onNavigate(item.id)}
      style={{ 
        borderTop: `2px solid transparent`,
        transition: "all 0.3s ease"
      }}
      cover={
        <div className="image-container" style={{ 
          height: "300px", 
          overflow: "hidden", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center", 
          background: "#f0f2f5",
          padding: 0,
          margin: 0
        }}>
          {renderImageContent()}
        </div>
      }
      styles={{ body: { padding: "12px" } }}
    >
      <div className="flex flex-col">
        {/* Header with System Name and Disposition Tag */}
        <div className="mb-2 flex justify-between items-center">
          <Typography.Title level={5} className="m-0">
                      {item.system_name}
          </Typography.Title>
          <Tag color={getDispositionColor(item.disposition)} >
            {item.disposition}
          </Tag>
        </div>
        
        {/* Details using Descriptions component */}
        <Descriptions
          size="small"
          column={1}
          bordered
          className="mt-2"
          styles={{ label: { fontWeight: "normal" },
                   content: { textAlign: "right" } }}
        >
          <Descriptions.Item label="Timestamp">{item.timestamp}</Descriptions.Item>
          <Descriptions.Item label="Media ID">{item.media_id}</Descriptions.Item>
          <Descriptions.Item label="Model">{item.model}</Descriptions.Item>
          <Descriptions.Item label="Part Name">{item.part_name}</Descriptions.Item>
          <Descriptions.Item label="Part Number">{item.part_number}</Descriptions.Item>
          <Descriptions.Item label="Camera">{item.camera}</Descriptions.Item>
        </Descriptions>
      </div>
    </AntCard>
  );
};

export const OutflowPreviewMeta: React.FC<GalleryCard<OutflowImageProps>> = ({ item, onNavigate }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    const image = new Image();
    image.src = item.path;
    image.onload = () => {
      const devicePixelRatio = window.devicePixelRatio || 1;
      // Use canvas container width (or default to 400) for thumbnail size
      const thumbnailWidth = canvas.clientWidth || 400;
      const thumbnailHeight = (image.height / image.width) * thumbnailWidth;

      // Set canvas size for rendering (scaled for high DPI)
      canvas.width = thumbnailWidth * devicePixelRatio;
      canvas.height = thumbnailHeight * devicePixelRatio;
      canvas.style.width = `${thumbnailWidth}px`;
      canvas.style.height = `${thumbnailHeight}px`;

      // Scale the drawing context for high DPI displays
      context.scale(devicePixelRatio, devicePixelRatio);

      // Draw the image on the canvas
      context.drawImage(image, 0, 0, thumbnailWidth, thumbnailHeight);

      // Draw defect boxes if they exist
      const defects = item.defects;
      if (defects) {
        context.strokeStyle = "red";
        context.lineWidth = 1;
        context.globalAlpha = 0.3;
        context.fillStyle = "rgba(255, 0, 0, 0.3)";

        defects.forEach((defect) => {
          console.log(defect)
        // Convert absolute pixel values to percentages of the full image
        const xPercent = defect.x / image.width;
        const yPercent = defect.y / image.height;
        const wPercent = defect.w / image.width;
        const hPercent = defect.h / image.height;

        // Draw filled rectangle scaled to the thumbnail dimensions
        context.fillRect(
          xPercent * thumbnailWidth,
          yPercent * thumbnailHeight,
          wPercent * thumbnailWidth,
          hPercent * thumbnailHeight
        );

        // Draw border rectangle on top
        context.globalAlpha = 1; // Fully opaque for the border
        context.strokeRect(
          xPercent * thumbnailWidth,
          yPercent * thumbnailHeight,
          wPercent * thumbnailWidth,
          hPercent * thumbnailHeight
        );
      });
        // Reset alpha for future drawing operations
        context.globalAlpha = 1;
      }
    };
  }, [item]);

  return (
    <AntCard
      hoverable
      className="outflow-card image-preview-card overflow-hidden rounded-lg shadow-md transition-all w-full"
      onClick={() => onNavigate(item.id)}
      style={{
        borderTop: "2px solid transparent",
        transition: "all 0.3s ease",
      }}
      cover={
        <div
          className="image-container"
          style={{
            height: "300px",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f0f2f5",
            padding: 0,
            margin: 0,
          }}
        >
          <canvas ref={canvasRef} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
      }
      bodyStyle={{ padding: "12px" }}
    >
      <div className="flex flex-col">
      <div className="mb-2 flex justify-between items-center">
          <Typography.Title level={5} className="m-0">
                      {item.media_id}
          </Typography.Title>
        </div>
        <Descriptions size="small" column={1} bordered className="mt-2" contentStyle={{ textAlign: "right" }} labelStyle={{ fontWeight: "normal" }}>
          <Descriptions.Item label="Timestamp">{item.timestamp}</Descriptions.Item>
          <Descriptions.Item label="Reviewed"><Tag color={get_review_style(item.review_status)}>
          {item.review_status ? "Yes" : "No"}
          </Tag></Descriptions.Item>
          <Descriptions.Item label="Model">{item.model}</Descriptions.Item>
          <Descriptions.Item label="Part Name">{item.part_name}</Descriptions.Item>
          <Descriptions.Item label="Part Number">{item.part_number}</Descriptions.Item>
          <Descriptions.Item label="Camera">{item.camera}</Descriptions.Item>
        </Descriptions>
      </div>
    </AntCard>
  );
};


export const ImageMetadata: React.FC<MetadataProps> = ({ 
  image,
  mutable_disposition, 
  updateDisposition, 
  className,
  onPrevious,
  onNext,
  onBack, 
  hasPrevious,
  hasNext,
}: MetadataProps) => {
  // Function to get tag color based on disposition
   const [isDatasetModalOpen, setIsDatasetModalOpen] = useState(false);
  const getDispositionColor = (disposition: string) => {
    switch(disposition) {
      case 'Part Okay':
        return 'success';
      case 'Rework':
        return 'warning';
      case 'Skip':
        return 'default';
      case 'Scrap':
        return 'error';
      default:
        return 'default';
    }
  };

    // inside ImageMetadata component
    const handleDispositionChange = (value: string) => {
    let confirmModal = Modal.confirm({
      title: 'Change Disposition?',
      content: `Do you want to change the disposition to "${value}"?`,
      okText: 'Yes, change it!',
      cancelText: 'No, keep it',
      // return a promise so the modal shows a loading spinner on the OK button
      onOk: async () => {
        // disable Cancel and show spinner on OK
        confirmModal.update({
          okButtonProps: { loading: true } as any,
          cancelButtonProps: { disabled: true },
        });

        try {
          // perform your API call
          await updateDisposition(value);
          // close the confirm dialog
          confirmModal.destroy();
          // show a quick toast
          message.success('Disposition updated successfully!');
        } catch (err: any) {
          // close the confirm dialog
          confirmModal.destroy();
          // show an error dialog
          Modal.error({
            title: 'Update failed',
            content: err.message || 'Could not change disposition.',
          });
        }
      },
    });
  };

  // Updated ImageMetadata component with enhanced defect display
  const renderDefectDetails = () => {
    if (!image.defects || !Array.isArray(image.defects) || image.defects.length === 0) {
      return (
        <div className="text-center py-8">
          <InfoCircleOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
          <p className="mt-2 text-gray-500">No defects detected</p>
        </div>
      );
    }

    const getConfidenceColor = (confidence: number) => {
      if (confidence >= 0.8) return '#52c41a'; // green
      if (confidence >= 0.6) return '#faad14'; // orange
      return '#ff4d4f'; // red
    };

    const getConfidenceTag = (confidence: number) => {
      if (confidence >= 0.8) return { color: 'success', text: 'High' };
      if (confidence >= 0.6) return { color: 'warning', text: 'Medium' };
      return { color: 'error', text: 'Low' };
    };

    return (
      <div className="space-y-3">
        {image.defects.map((defect, index) => {
          const confidenceTag = getConfidenceTag(defect.confidence);
          
          return (
            <AntCard 
              key={`defect-detail-${index}`} 
              size="small"
              className="shadow-sm hover:shadow-md transition-shadow duration-200"
              style={{ 
                borderRadius: '8px',
                border: '1px solid #f0f0f0'
              }}
              title={
                <div className="flex items-center justify-between">
                  <Space>
                    <Badge 
                      count={index + 1} 
                      style={{ backgroundColor: '#1890ff' }}
                    />
                    <Text strong>Defect Detection</Text>
                  </Space>
                  {defect.comment && (
                    <CommentOutlined 
                      style={{ 
                        color: '#52c41a', 
                        fontSize: '16px',
                        padding: '4px',
                        backgroundColor: '#f6ffed',
                        borderRadius: '50%'
                      }} 
                    />
                  )}
                </div>
              }
            >
              <div className="space-y-3">
                {/* Metrics Row */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <Text type="secondary" className="text-xs block">Position</Text>
                    <Text strong className="text-sm">({defect.x}, {defect.y})</Text>
                  </div>
                  <div className="text-center">
                    <Text type="secondary" className="text-xs block">Dimensions</Text>
                    <Text strong className="text-sm">{defect.w} × {defect.h}</Text>
                  </div>
                  <div className="text-center">
                    <Text type="secondary" className="text-xs block">Confidence</Text>
                    <div className="flex flex-col items-center">
                      <Text 
                        strong 
                        style={{ color: getConfidenceColor(defect.confidence) }}
                        className="text-sm"
                      >
                        {(defect.confidence * 100).toFixed(1)}%
                      </Text>
                      <Tag color={confidenceTag.color} className="mt-1">
                        {confidenceTag.text}
                      </Tag>
                    </div>
                  </div>
                </div>

                {/* Comment Section */}
                {defect.comment && (
                  <>
                    <Divider style={{ margin: '12px 0' }} />
                    <div>
                      <div className="flex items-center mb-2">
                        <CommentOutlined style={{ color: '#1890ff', marginRight: '6px' }} />
                        <Text strong className="text-sm">Inspector Notes</Text>
                      </div>
                      <div 
                        className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-lg border-l-4 border-blue-400"
                        style={{ 
                          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                          borderLeft: '4px solid #3b82f6'
                        }}
                      >
                        <Paragraph 
                          className="mb-0 text-sm leading-relaxed"
                          style={{ 
                            color: '#374151',
                            fontStyle: 'italic',
                            lineHeight: '1.6'
                          }}
                        >
                          "{defect.comment}"
                        </Paragraph>
                      </div>
                    </div>
                  </>
                )}

                {/* No Comment Placeholder */}
                {!defect.comment && (
                  <>
                    <Divider style={{ margin: '12px 0' }} />
                    <div className="text-center py-2">
                      <Text type="secondary" className="text-xs italic">
                        Click defect in image to add inspector notes
                      </Text>
                    </div>
                  </>
                )}
              </div>
            </AntCard>
          );
        })}
      </div>
    );
  };


  const items: DescriptionsProps['items'] = [
    {
      key: '1',
      label: 'Timestamp',
      children: image.timestamp,
    },
    {
      key: '2',
      label: 'Media Id',
      children: image.media_id,
    },
    {
      key: '3',
      label: 'Model',
      children: image.model,
    },
    {
      key: '4',
      label: 'Part Name',
      children: image.part_name,
    },
    {
      key: '5',
      label: 'Part Number',
      children: image.part_number,
    },
    {
      key: '6',
      label: 'Disposition',
      children: mutable_disposition ? (
        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel id="disposition-label"></InputLabel>
          <Select
            labelId="disposition-label"
            value={image.disposition}
            onChange={e => handleDispositionChange(e.target.value)}
          >
            <MenuItem value="Part Okay">Part Okay</MenuItem>
            <MenuItem value="Rework">Rework</MenuItem>
            <MenuItem value="Skip">Skip</MenuItem>
            <MenuItem value="Scrap">Scrap</MenuItem>
          </Select>
        </FormControl>
      ) : (
        <Tag color={getDispositionColor(image.disposition)}>
          {image.disposition}
        </Tag>
      ),
    },
    {
      key: '7',
      label: 'Camera',
      children: image.camera,
    },
    {
      key: '8',
      label: 'Defect Details',
      children: renderDefectDetails(),
    }
  ];

  console.log("ImageMetadata items:", image.defects);
  const descriptionsStyle = {
    backgroundColor: 'white',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    borderRadius: '6px',
    padding: '16px',
  };
  if (!mutable_disposition) {
    return (
      <Card className={className}>
        <CardContent>
          <Descriptions
            style={descriptionsStyle}
            title="Image Information"
            bordered
            column={1}
            size="small"
            items={items}
          />
        </CardContent>
      </Card>
    );
  }

  const submitDatasetAddition = async (dataset_id: string) => {
    // console.log('Adding selected images to dataset:', dataset_id);

    const imageData = [{
      id: image.id,
      system_name: image.system_name
    }]

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/datasets`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          image_data: imageData,
          dataset_id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add images to dataset');
      }

      const datasetResponse = await response.json();

      setIsDatasetModalOpen(false);

      return datasetResponse;
    } catch (error) {
      console.error('Error adding images to dataset:', error);
    }
  };

  const submitDatasetCreation = async (name: string, description: string) => {
    try {
      
      // Extract the data in the format expected by your API
      const imageData = [{
        id: image.id,
        system_name: image.system_name,
      }]

      // console.log('Creating dataset with image data:', imageData);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/datasets`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          description,
          image_data: imageData, // Send the paired image ID and system name
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to create dataset');
      }
      const newDataset = await response.json();
      setIsDatasetModalOpen(false);
      
      // You might want to show a success notification here
      return newDataset;
    } catch (error) {
      console.error('Error creating dataset:', error);
      // Handle error - show toast/notification
    }
  };

  return (
    <div className={`${className} relative flex flex-col h-full`}>
      <div className="flex-1 overflow-auto p-4">
        <Descriptions
          style={descriptionsStyle}
          title="Image Information"
          bordered
          column={1}
          size="default"
          items={items}
        />
      </div>
      <div className="sticky bottom-0 bg-gray-300 p-2">
        <div className="mb-2">
          <AntButton
            type="default"
            onClick={onBack}
            style={{
              width: '100%',
              height: '40px',
            }}
          >
            ← Back to Gallery
          </AntButton>
        </div>
        {/* Row for the edit buttons */}
        <div className="flex gap-2 mb-2">
          <div className="w-1/2 mt-2">
            <EditLabelModal image={image} />
          </div>
          <div className="w-1/2 mt-2">
            <AntButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsDatasetModalOpen(true)}
              style={{ 
                backgroundColor: '#4CAF50', 
                borderColor: '#4CAF50',
                width: '100%',
                height: '40px'
              }}
              // className="hover:bg-green-600"
            >
            ADD TO DATASET
          </AntButton>
          </div>
        </div>
        <DatasetCreationModal
        isOpen={isDatasetModalOpen}
        onClose={() => setIsDatasetModalOpen(false)}
        selectedCount={1}
        onCreateDataset={submitDatasetCreation}
        selectedImages={[{ id: image.id, system_name: image.system_name }]}
        onAddToDataset={submitDatasetAddition}
      />
        {/* Row for navigation buttons */}
        <div className="flex gap-2">
          <div className="w-1/2">
            <AntButton 
              icon={<LeftOutlined />}
              style={{ 
                backgroundColor: '#2E86C1', 
                borderColor: '#2E86C1',
                width: '100%',
                height: '40px'
              }}
              className="hover:bg-gray-200"
              onClick={onPrevious}
              disabled={!hasPrevious}
            >
              Previous
            </AntButton>
          </div>
          <div className="w-1/2">
            <AntButton 
              icon={<RightOutlined />}
              style={{ 
                backgroundColor: '#2E86C1', 
                borderColor: '#2E86C1',
                width: '100%',
                height: '40px'
              }}
              className="hover:bg-blue-600"
              onClick={onNext}
              disabled={!hasNext}
            >
              Next
            </AntButton>
          </div>
        </div>
      </div>
    </div>
  );
};
